import React, { useState, useEffect } from "react";
import { api } from "../utils/api";
import { showErrorToast, showSuccessToast } from "../utils/toast";
import Loading from "./Loading";
import { VEHICLE_TYPE_OPTIONS } from "../utils/mapNevdisBodyType";
import {
  catalogPriceFromProduct,
  dealerPriceForSelection,
  formatMoney,
  parsePriceOverrideInput,
  productPriceFieldCopy,
  readPriceOverride
} from "../utils/signupLinkPrice";
import { resolvePartnerCompanyId } from "../utils/signupLinkAccess";

const CreateSignUpModal = ({
  isOpen,
  onClose,
  partnerProducts,
  partnerUsers,
  onSignUpCreated,
  existingLink,
  selectedCompany
}) => {
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedPartnerUser, setSelectedPartnerUser] = useState(null);
  const [rrp, setRrp] = useState("");
  const [minRrp, setMinRrp] = useState(0);
  const [lockInPeriodMonths, setLockInPeriodMonths] = useState("");
  const [paidMonthly, setPaidMonthly] = useState(false);
  const [vehicleType, setVehicleType] = useState("");
  const [priceOverride, setPriceOverride] = useState("");
  const [priceOverrideError, setPriceOverrideError] = useState("");
  const [loading, setLoading] = useState(false);

  const parseLockInPeriodMonths = (value) => {
    if (value === "" || value == null) return null;
    const n = parseInt(String(value), 10);
    return Number.isFinite(n) && n > 0 ? n : null;
  };

  useEffect(() => {
    if (!isOpen) {
      setSelectedProduct(null);
      setSelectedPartnerUser(null);
      setRrp("");
      setMinRrp(0);
      setLockInPeriodMonths("");
      setPaidMonthly(false);
      setVehicleType("");
      setPriceOverride("");
      setPriceOverrideError("");
      setLoading(false);
    } else if (existingLink) {
      // Pre-fill values if editing an existing signup link
      let product = partnerProducts.find((p) => {
        if (existingLink.idProduct && String(p._id) === String(existingLink.idProduct._id || existingLink.idProduct)) {
          return true;
        }
        if (
          existingLink.idSubscription &&
          String(p._id) === String(existingLink.idSubscription._id || existingLink.idSubscription)
        ) {
          return true;
        }
        return false;
      });

      // Fallback when partner pricing isn't configured yet — still allow edit
      if (!product) {
        product =
          existingLink.idSubscription ||
          existingLink.idProduct ||
          null;
      }

      const partnerUser = partnerUsers.find(
        (partner) =>
          existingLink.idUser &&
          String(partner._id) === String(existingLink.idUser._id || existingLink.idUser)
      );

      if (product) {
        setSelectedPartnerUser(partnerUser ? String(partnerUser._id) : null);
        setSelectedProduct(product);
        const basePrice =
          typeof product.partnerPricing === "number"
            ? product.partnerPricing
            : typeof product.price === "number"
              ? product.price
              : parseFloat(product.price) || 0;
        // Promo / paid-monthly links use annual RRP which can be below merchant product.price
        const existingRrp = Number(existingLink.rrp) || 0;
        setMinRrp(
          existingLink.paidMonthly || existingLink.lockInPeriodMonths
            ? Math.min(basePrice, existingRrp)
            : basePrice
        );
        setRrp(existingRrp.toFixed(2));
        setLockInPeriodMonths(
          existingLink.lockInPeriodMonths != null
            ? String(existingLink.lockInPeriodMonths)
            : ""
        );
        setPaidMonthly(Boolean(existingLink.paidMonthly));
        setVehicleType(existingLink.vehicleType || "");
        const existingOverride = readPriceOverride(existingLink);
        setPriceOverride(existingOverride != null ? formatMoney(existingOverride) : "");
        setPriceOverrideError("");
      }
    }
  }, [isOpen, existingLink, partnerProducts, partnerUsers]);

  const handleProductSelection = (e) => {
    const selected = partnerProducts.find(
      (p) => String(p._id) === String(e.target.value)
    );
    if (selected) {
      // Use partnerPricing if available, otherwise use the base price
      const basePrice = selected.partnerPricing || selected.price;
      console.log("Selected product:", selected.name, "Base price:", basePrice, "Type:", typeof basePrice);

      // Ensure basePrice is a number
      const numericPrice = typeof basePrice === "number" ? basePrice : parseFloat(basePrice) || 0;
      console.log("Numeric price:", numericPrice);

      setSelectedProduct(selected);
      setMinRrp(numericPrice);
      setRrp(numericPrice.toFixed(2)); // Ensure decimal formatting
      setLockInPeriodMonths(
        selected.lockInPeriodMonths != null ? String(selected.lockInPeriodMonths) : ""
      );
      setPaidMonthly(selected.interval === "month");
      if (!existingLink) {
        setVehicleType("");
      }
    } else {
      setSelectedProduct(null);
      setMinRrp(0);
      setRrp("");
      setLockInPeriodMonths("");
      setPaidMonthly(false);
      setVehicleType("");
    }
  };

  const handlePartnerUserSelect = (e) => {
    console.log("partner user: ", e.target.value);
    setSelectedPartnerUser(e.target.value || null);
  };

  const handleSubmit = async () => {
    if (!selectedProduct || rrp === "") {
      showErrorToast("Please select a product and enter an RRP.");
      return;
    }

    const parsedOverride = parsePriceOverrideInput(priceOverride);
    if (!parsedOverride.ok) {
      setPriceOverrideError(parsedOverride.error);
      showErrorToast(parsedOverride.error);
      return;
    }
    setPriceOverrideError("");

    // Promo / paid-monthly links (and any edit of an existing payment link) may
    // use an annual RRP below the merchant product.price — do not block those.
    const isEditing = Boolean(existingLink);
    if (
      !isEditing &&
      !paidMonthly &&
      !parseLockInPeriodMonths(lockInPeriodMonths) &&
      parseFloat(rrp) < minRrp
    ) {
      showErrorToast(`Please enter an RRP of at least $${minRrp}.`);
      return;
    }

    setLoading(true);
    // The /self-signup-customer route does not pass selectedCompany.
    const idPartnerCompany = resolvePartnerCompanyId(selectedCompany);
    const idPartnerUser =
      typeof selectedPartnerUser === "object" && selectedPartnerUser
        ? selectedPartnerUser._id || selectedPartnerUser.value || null
        : selectedPartnerUser || null;

    // Prefer explicit type; when editing fall back to which field the link uses
    // (pricing payloads / populated docs sometimes omit type).
    const productId = selectedProduct._id || selectedProduct;
    const isSubscription =
      selectedProduct.type === "subscription" ||
      Boolean(existingLink?.idSubscription && !existingLink?.idProduct);
    const idSubscription = isSubscription ? productId : null;
    const idProduct = isSubscription ? null : productId;

    const resolvedLockInPeriodMonths = parseLockInPeriodMonths(lockInPeriodMonths);
    const resolvedVehicleType = vehicleType || null;
    // null clears a saved override so the link falls back to the dealer price.
    const resolvedPriceOverride = parsedOverride.value;

    const payload = {
      idPartnerCompany,
      idSubscription,
      rrp: parseFloat(rrp),
      idPartnerUser,
      idProduct,
      lockInPeriodMonths: resolvedLockInPeriodMonths,
      paidMonthly,
      vehicleType: resolvedVehicleType,
      priceOverride: resolvedPriceOverride,
    };

    try {
      let response;
      if (existingLink) {
        response = await api(`v1/selfsignup/${existingLink._id}`, "PUT", payload);
      } else {
        response = await api("v1/partner/generate-signup-link", "POST", payload);
      }

      if (response?.data?.success) {
        showSuccessToast(existingLink ? "Signup link updated successfully!" : "Signup link created successfully!");
        console.log("Calling onSignUpCreated callback to refresh the list");
        onSignUpCreated();
        onClose();
      } else {
        showErrorToast(
          response?.data?.message ||
            response?.data?.error ||
            "Failed to process signup link."
        );
      }
    } catch (error) {
      console.error("Error processing signup link:", error);
      showErrorToast(
        error?.response?.data?.message || "Error processing signup link!"
      );
    }
    setLoading(false);
  };

  return (
    <div
      className={`fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center ${
        isOpen ? "block" : "hidden"
      } z-10`}
    >
      <div className="bg-white rounded-lg shadow-lg max-w-3xl w-full p-6 max-h-[85%] overflow-y-scroll">
        <div className="flex flex-col w-full border-b pb-3">
          <div className="flex flex-row justify-between">
            <h2 className="text-lg font-semibold">{existingLink ? "Edit Sign Up Link" : "Create Self Sign Up Link"}</h2>
            <button className="text-gray-500 hover:text-gray-700 text-[40px] mt-[-20px]" onClick={onClose}>
              &times;
            </button>
          </div>
          <ProductPriceField
            selectedProduct={selectedProduct}
            existingLink={existingLink}
            priceOverride={priceOverride}
            priceOverrideError={priceOverrideError}
            onChange={(value) => {
              setPriceOverride(value);
              setPriceOverrideError("");
            }}
            onUseDefault={() => {
              setPriceOverride("");
              setPriceOverrideError("");
            }}
          />
        </div>

        <div className="mt-4 flex flex-col gap-4">
          <div>
            <label className="block text-sm font-semibold mb-1">Select Partner User (Optional)</label>
            <select
              className="border rounded px-3 py-2 w-full"
              value={
                selectedPartnerUser
                  ? typeof selectedPartnerUser === "object"
                    ? selectedPartnerUser._id
                    : selectedPartnerUser
                  : ""
              }
              onChange={handlePartnerUserSelect}
            >
              <option value="">-- No Partner User Selected --</option>
              {partnerUsers.map((partner) =>
                partner.active ? (
                  <option key={partner._id} value={partner._id}>
                    {`${partner.firstName} ${partner.lastName}`}
                  </option>
                ) : null
              )}
            </select>
            <p className="text-sm text-gray-600 mt-1">
              Partner user is optional. Leave unselected if no specific user should be assigned.
            </p>
          </div>

          <div>
            <label className="block text-sm font-semibold mb-1" htmlFor="signup-product">
              Select Product
            </label>
            <select
              id="signup-product"
              className="border rounded px-3 py-2 w-full"
              value={selectedProduct ? selectedProduct._id : ""}
              onChange={handleProductSelection}
            >
              <option value="">-- Select Product --</option>
              {(() => {
                const options = [...partnerProducts];
                const existingProduct =
                  existingLink?.idSubscription || existingLink?.idProduct;
                if (
                  existingProduct &&
                  !options.some((p) => String(p._id) === String(existingProduct._id || existingProduct))
                ) {
                  options.unshift(existingProduct);
                }
                return options.map((product) =>
                  product.price != null ||
                  product.partnerPricing != null ||
                  product.merchantPrice != null ||
                  existingLink ? (
                    <option key={product._id} value={product._id}>
                      {product.name}
                    </option>
                  ) : null
                );
              })()}
            </select>
          </div>

          <div>
            <label className="block text-sm font-semibold mb-1">RRP Price</label>
            <input
              type="number"
              className="border rounded px-3 py-2 w-full"
              placeholder="Enter RRP (e.g., 99.99)"
              value={rrp}
              onChange={(e) => setRrp(e.target.value)}
              min={existingLink || paidMonthly ? 0 : minRrp}
              step="0.01"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold mb-1">
              Lock-in Period (months)
            </label>
            <input
              type="number"
              className="border rounded px-3 py-2 w-full"
              placeholder="No lock-in (optional)"
              value={lockInPeriodMonths}
              onChange={(e) => setLockInPeriodMonths(e.target.value)}
              min={1}
              step="1"
            />
            <p className="text-sm text-gray-600 mt-1">
              Leave blank for no lock-in. Common values: 6 or 12.
            </p>
          </div>

          <div>
            <label className="flex items-center gap-2 text-sm font-semibold">
              <input
                type="checkbox"
                className="h-4 w-4"
                checked={paidMonthly}
                onChange={(e) => setPaidMonthly(e.target.checked)}
              />
              Paid monthly
            </label>
            <p className="text-sm text-gray-600 mt-1">
              When checked, Stripe bills monthly (RRP ÷ 12). Lock-in under 12 months also bills monthly.
            </p>
          </div>

          <div>
            <label className="block text-sm font-semibold mb-1">
              Vehicle type (variant)
            </label>
            <select
              className="border rounded px-3 py-2 w-full"
              value={vehicleType}
              onChange={(e) => setVehicleType(e.target.value)}
            >
              <option value="">-- Any / not locked --</option>
              {VEHICLE_TYPE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <p className="text-sm text-gray-600 mt-1">
              Locks this payment link to one vehicle tier (e.g. Sparkle Sedan vs SUV).
            </p>
          </div>

          <button
            className={`px-4 py-2 rounded ${
              !selectedProduct ? "bg-gray-400 cursor-not-allowed" : "bg-blue-500 text-white"
            }`}
            onClick={handleSubmit}
            disabled={!selectedProduct || loading}
          >
            {loading ? "Processing..." : existingLink ? "Update Sign Up Link" : "Create Sign Up Link"}
          </button>

          {loading && <Loading />}
        </div>
      </div>
    </div>
  );
};

function ProductPriceField({
  selectedProduct,
  existingLink,
  priceOverride,
  priceOverrideError,
  onChange,
  onUseDefault
}) {
  const dealerPrice = dealerPriceForSelection(selectedProduct, existingLink);
  const catalogPrice = dealerPrice == null ? catalogPriceFromProduct(selectedProduct) : null;
  const copy = productPriceFieldCopy({
    dealerPrice,
    catalogPrice,
    overrideValue: priceOverride
  });
  const error = priceOverrideError || copy.error;

  return (
    <div className="mt-3">
      <label className="block text-sm font-semibold mb-1" htmlFor="signup-product-price">
        Product Price
      </label>
      <input
        id="signup-product-price"
        type="number"
        className="border rounded px-3 py-2 w-full"
        placeholder={copy.placeholder}
        value={priceOverride}
        onChange={(e) => onChange(e.target.value)}
        min="0.01"
        step="0.01"
        aria-invalid={error ? "true" : "false"}
        aria-describedby="signup-product-price-help"
      />
      <p id="signup-product-price-help" className="text-sm text-gray-600 mt-1">
        {copy.helper}
      </p>
      {error ? <p className="text-sm text-red-600 mt-1">{error}</p> : null}
      {priceOverride !== "" ? (
        <button type="button" className="text-sm text-blue-600 mt-1" onClick={onUseDefault}>
          {dealerPrice != null ? "Use dealer price" : "Use default price"}
        </button>
      ) : null}
    </div>
  );
}

export default CreateSignUpModal;
